import React from 'react';
import { Clock, MessageCircle, Zap, Sparkle, ShieldCheck, Smartphone } from 'lucide-react';

const FeatureCard: React.FC<{ icon: React.ReactNode, title: string, description: string }> = ({ icon, title, description }) => (
  <div className="p-8 md:p-10 bg-white rounded-[2rem] border border-[#1F2933]/5 card-shadow transition-all hover:translate-y-[-4px]">
    <div className="w-12 h-12 md:w-14 md:h-14 bg-[#F4F1EC] text-[#7FAF9A] rounded-2xl flex items-center justify-center mb-6 md:mb-8">
      {icon}
    </div>
    <h3 className="text-xl md:text-2xl font-extrabold text-[#1F2933] mb-3 md:mb-4">{title}</h3>
    <p className="text-[#1F2933]/60 leading-relaxed text-base md:text-lg font-medium">{description}</p>
  </div>
);

const Features: React.FC = () => {
  return (
    <section id="how-it-works" className="py-24 md:py-32 px-6">
      <div className="max-w-7xl mx-auto">
        <div className="max-w-4xl mb-16 md:mb-20">
          <h2 className="text-4xl md:text-7xl font-extrabold text-[#1F2933] mb-6 leading-[1.1] tracking-tight">
            We help you <br className="hidden md:block" />
            <span className="text-[#7FAF9A]">take back your evenings.</span>
          </h2>
        </div>
        
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-10">
          <FeatureCard 
            icon={<Clock size={24} />}
            title="Automated Admin"
            description="Let AI handle the repetitive forms and data entry so you can close your laptop by 4pm."
          />
          <FeatureCard 
            icon={<MessageCircle size={24} />}
            title="Calm Support"
            description="Intelligent chat that answers common questions humanely, only alerting you when it's critical."
          />
          <FeatureCard 
            icon={<Smartphone size={24} />}
            title="Zero Install"
            description="People are tired of app stores. Your tools run instantly in the browser—one link, zero friction."
          />
          {/* Hide less critical features on small mobile screens to reduce scrolling fatigue */}
          <div className="hidden sm:block">
            <FeatureCard 
              icon={<Sparkle size={24} />}
              title="No App Fatigue"
              description="Don't lose customers to the 'Install' button. Your business is just one tap away from being used."
            />
          </div>
          <div className="hidden lg:block">
            <FeatureCard 
              icon={<Zap size={24} />}
              title="Rapid Delivery"
              description="Systems designed for speed. We build and deploy within weeks, not months."
            />
          </div>
          <div className="hidden sm:block">
            <FeatureCard 
              icon={<ShieldCheck size={24} />}
              title="Safe & Private"
              description="Your data stays yours. We use secure standards to keep your business life private."
            />
          </div>
        </div>
      </div>
    </section>
  );
};

export default Features;
